/**
 * The form model (`specs/M4-V8-forms.md` §4).
 *
 * A form is a board view whose questions are board columns. Two rules from the discovery pass
 * shape every type below:
 *   - **the label belongs to the form, not the column** — renaming a column must not rename a
 *     question `[discovery/forms/README.md]`;
 *   - **monday's `mandatory` is the required flag**; its newer `required` key stayed `false` even
 *     after the builder's *Required* switch was turned on (q295), so it is preserved in `extras`
 *     and never read.
 */
import { z } from 'zod';
/**
 * The 24 question kinds monday's *Add content* offers, which are exactly the 2026-10 SDL's
 * `FormQuestionType` values `[cap:sandbox-add-content.json]`, in our snake_case.
 */
export const FORM_QUESTION_KINDS = [
    'name',
    'short_text',
    'long_text',
    'number',
    'email',
    'phone',
    'link',
    'location',
    'country',
    'single_select',
    'multi_select',
    'date',
    'date_range',
    'hour',
    'boolean',
    'rating',
    'people',
    'file',
    'signature',
    'connected_boards',
    'subitems',
    'updates',
    'display_text',
    'page_block',
];
export const formQuestionKindSchema = z.enum(FORM_QUESTION_KINDS);
/** The SDL spelling, for `/v2`'s `FormQuestionType`. */
export const FORM_QUESTION_KIND_TO_SDL = {
    name: 'Name',
    short_text: 'ShortText',
    long_text: 'LongText',
    number: 'Number',
    email: 'Email',
    phone: 'Phone',
    link: 'Link',
    location: 'Location',
    country: 'Country',
    single_select: 'SingleSelect',
    multi_select: 'MultiSelect',
    date: 'Date',
    date_range: 'DateRange',
    hour: 'HOUR',
    boolean: 'Boolean',
    rating: 'Rating',
    people: 'People',
    file: 'File',
    signature: 'Signature',
    connected_boards: 'ConnectedBoards',
    subitems: 'Subitems',
    updates: 'Updates',
    display_text: 'DISPLAY_TEXT',
    page_block: 'PAGE_BLOCK',
};
export const SDL_TO_FORM_QUESTION_KIND = Object.fromEntries(Object.entries(FORM_QUESTION_KIND_TO_SDL).map(([kind, sdl]) => [sdl, kind]));
/** Kinds that are not bound to a board column and therefore write no cell. */
export const CONTENT_BLOCK_KINDS = new Set(['display_text', 'page_block']);
/** Kinds bound to something other than an ordinary column value. */
export const PSEUDO_QUESTION_KINDS = new Set([
    'display_text',
    'page_block',
    'updates',
]);
export const formQuestionSettingsSchema = z.looseObject({
    /** Single/multi select: how the options are laid out (`display` in monday's JSON). */
    display: z.enum(['vertical', 'horizontal', 'dropdown']).optional(),
    optionsOrder: z.enum(['custom', 'alphabetical']).optional(),
    /** Character limit rendered as the live `0/N` counter. */
    limit: z.number().int().positive().optional(),
    includeTime: z.boolean().optional(),
    defaultAnswer: z.string().nullable().optional(),
    prefill: z.boolean().optional(),
    checkedByDefault: z.boolean().optional(),
    skipValidation: z.boolean().optional(),
});
export const formQuestionOptionSchema = z.object({
    /** monday's label index for a status column, or the dropdown option id, as a string. */
    value: z.string(),
    label: z.string(),
    position: z.number().int().optional(),
    visible: z.boolean().default(true),
    /** Status labels carry a palette colour; a dropdown option does not. */
    color: z.string().nullable().optional(),
});
export const formQuestionSchema = z.object({
    /** The **board column id** for a bound question; a synthetic id for a content block. */
    id: z.string().min(1),
    kind: formQuestionKindSchema,
    /** The label. Owned by the form (§1), never read back from the column. */
    title: z.string(),
    description: z.string().nullable().default(null),
    visible: z.boolean().default(true),
    /** monday's `mandatory`. */
    required: z.boolean().default(false),
    /**
     * Marks **the** name question: a form has at most one, the builder cannot delete it and
     * *Add content* disables Name once it exists ("A form can have only one name question").
     * Deliberately **not** a required flag — monday leaves `mandatory` absent on plenty of name
     * questions and writing one here would break `settings_str` parity (q295).
     */
    structural: z.boolean().default(false),
    pageBlockId: z.string().default('page_block__classic_default'),
    /** Shown only when this rule matches (monday's `parentConditionalRuleId`). */
    showIfRuleId: z.string().nullable().default(null),
    options: z.array(formQuestionOptionSchema).default([]),
    settings: formQuestionSettingsSchema.default({}),
    /**
     * The question names a board column that **no longer exists**. monday keeps a question entry
     * forever: **344 of the 638** entries in the account's 38 form rows are orphans like this,
     * against the 216 questions the forms actually render. An orphan is preserved for round-trip
     * and never rendered, never validated and never written to a cell.
     * Only meaningful when the board's columns were supplied to `formFromMondayViewData()`.
     */
    orphan: z.boolean().default(false),
    /** Everything monday stored on the question that we do not model, kept for round-trip. */
    extras: z.record(z.string(), z.unknown()).default({}),
});
export const formConditionalRuleSchema = z.object({
    id: z.string().min(1),
    questionId: z.string().min(1),
    questionKind: formQuestionKindSchema,
    /** monday nests inconsistently — `["0"]` and `[["0"]]` both occur; normalised to `string[]`. */
    values: z.array(z.string()).default([]),
});
export const formSettingsSchema = z.object({
    includeNameQuestion: z.boolean().default(true),
    includeUpdateQuestion: z.boolean().default(false),
    isSurvey: z.boolean().default(false),
    isAnonymous: z.boolean().default(false),
    /** monday's `hideMondayLogo`; ours hides the product badge on the public page. */
    hideBranding: z.boolean().default(false),
    syncQuestionAndColumnTitles: z.boolean().default(false),
    submitAnother: z.enum(['none', 'click', 'auto']).default('none'),
    successTitle: z.string().nullable().default(null),
    successContent: z.string().nullable().default(null),
    redirect: z
        .object({ enabled: z.boolean().default(false), url: z.string().nullable().default(null) })
        .default({ enabled: false, url: null }),
    responseLimit: z
        .object({ enabled: z.boolean().default(false), limit: z.number().int().positive().nullable().default(null) })
        .default({ enabled: false, limit: null }),
    closeDate: z
        .object({ enabled: z.boolean().default(false), date: z.string().nullable().default(null) })
        .default({ enabled: false, date: null }),
    requireLogin: z.boolean().default(false),
    reCaptcha: z.boolean().default(false),
    conditionalRules: z.array(formConditionalRuleSchema).default([]),
    splitView: z.boolean().default(false),
});
export const formAppearanceSchema = z.object({
    /** monday's `rgba(…)` string, kept verbatim so an export is byte-identical. */
    headerColor: z.string().nullable().default(null),
    logoUrl: z.string().nullable().default(null),
    font: z.string().nullable().default(null),
    showProgressBar: z.boolean().default(false),
});
export const DEFAULT_FORM_SETTINGS = formSettingsSchema.parse({});
export const DEFAULT_FORM_APPEARANCE = formAppearanceSchema.parse({});
//# sourceMappingURL=types.js.map